<script lang="ts" setup>
// 参考: https://github.com/vueuse/gesture/blob/8b627bb2e388a0901b4f316c503b2e6516137898/docs/.vitepress/components/demos/PinchExample.vue
import { useDrag, usePinch } from '@vueuse/gesture';
import { useSpring } from '@vueuse/motion';
import { interpolate } from 'popmotion';

defineProps<{
  src: string;
}>();

const imageBox = ref<HTMLElement | null>(null);
const imageElement = ref<HTMLElement | null>(null);

const springValues = reactive<{
  x: number;
  y: number;
  zoom: number;
}>({ x: 0, y: 0, zoom: 0 });

const { set } = useSpring(springValues);

const remapZoom = interpolate([-2000, 0, 2000], [0.25, 1, 4], { clamp: true });

usePinch(
  ({ offset: [d, _a] }) => {
    const minZoom = 1; // 最小倍率を1に設定
    const zoom = Math.max(d, minZoom);
    set({ x: 0, y: 0, zoom });
  },
  {
    domTarget: imageBox,
    eventOptions: {
      passive: false,
    },
  }
);

useDrag(
  ({ offset: [dx, dy], event }) => {
    event.stopPropagation();

    const imageWidth = imageElement.value?.offsetWidth || 0;
    const imageHeight = imageElement.value?.offsetHeight || 0;
    const boxWidth = imageBox.value?.offsetWidth || 0;
    const boxHeight = imageBox.value?.offsetHeight || 0;

    // 画面サイズからはみ出た分だけ移動できるようにする
    const maxTranslateX = Math.max(
      0,
      (imageWidth * remapZoom(springValues.zoom) - boxWidth) / 2
    );
    const maxTranslateY = Math.max(
      0,
      (imageHeight * remapZoom(springValues.zoom) - boxHeight) / 2
    );
    const clampedX = Math.max(-maxTranslateX, Math.min(maxTranslateX, dx));
    const clampedY = Math.max(-maxTranslateY, Math.min(maxTranslateY, dy));

    set({ x: clampedX, y: clampedY });
  },
  {
    domTarget: imageBox,
    eventOptions: {
      passive: false,
    },
  }
);

// Disable viewport pinch zoom on whole app

const cancelEvent = (e: Event) => e.preventDefault();

onMounted(() => {
  window.addEventListener('wheel', cancelEvent, {
    passive: false,
  } as AddEventListenerOptions);
  document.addEventListener('gesturestart', cancelEvent);
  document.addEventListener('gesturechange', cancelEvent);
  document.addEventListener('dragstart', cancelEvent);
});

onBeforeUnmount(() => {
  window.removeEventListener('wheel', cancelEvent, {
    passive: false,
  } as AddEventListenerOptions);
  document.removeEventListener('gesturestart', cancelEvent);
  document.removeEventListener('gesturechange', cancelEvent);
  document.removeEventListener('dragstart', cancelEvent);
});
</script>

<template>
  <div ref="imageBox" class="image-box" style="overflow: hidden">
    <img
      ref="imageElement"
      class="image-element"
      :style="{
        transform: `
          translate(${springValues.x}px,${springValues.y}px)
          scale(${remapZoom(springValues.zoom)})
        `,
      }"
      :src="src"
      loading="lazy"
      decoding="async"
    />
  </div>
</template>

<style lang="scss">
.image-box {
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
}

.image-element {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
}
</style>
